import { motion } from "framer-motion";
import { MapPin, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { AREAS } from "@/data/areas";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { GygTourBand } from "@/components/GetYourGuide";
import { ExploreLinks } from "@/components/ExploreLinks";

export default function AreasPage() {
  return (
    <main data-testid="areas-page" className="pt-16 md:pt-20">
      <Seo
        title="Copenhagen Neighbourhoods & Area Guides"
        description="Discover Copenhagen's neighbourhoods — Inner city, Christianshavn, Nørrebro, Vesterbro and more — with the best things to see, eat and do in each."
        path="/areas"
        image="https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/9eyx3e0p_2026-08-17%2000_00_57.118%2B0200.webp"
      />
      <PageSchema
        name="Copenhagen Neighbourhoods & Area Guides"
        description="Discover Copenhagen's neighbourhoods — Inner city, Christianshavn, Nørrebro, Vesterbro and more — with the best things to see, eat and do in each."
        path="/areas"
        crumb="Areas"
      />
      {/* page header */}
      <section className="relative overflow-hidden bg-[#13141A] text-white border-b border-white/20">
        <div className="absolute inset-0">
          <img src={"https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/9eyx3e0p_2026-08-17%2000_00_57.118%2B0200.webp"} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="areas-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="areas-share" />
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            All 12 areas of<br />
            <span className="font-editorial italic font-600">Copenhagen</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6">
            Every district has its own character. Here&apos;s the full map — from the medieval Indre By to
            the post-industrial playground of Refshaleøen — with exactly what to find in each.
          </p>
        </div>
      </section>

      {/* editorial list */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 space-y-16 md:space-y-28">
          {AREAS.map((a, i) => {
            const flip = i % 2 === 1;
            return (
              <Reveal key={a.id} y={60}>
                <div
                  id={a.id}
                  data-testid={`area-block-${a.id}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-center"
                >
                  <div className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
                    <Link to={`/areas/${a.id}`} data-testid={`area-image-link-${a.id}`} className="block relative border border-zinc-200 shadow-brut-lg overflow-hidden aspect-[16/10] group">
                      <img
                        src={a.image}
                        alt={`${a.name}, a Copenhagen neighbourhood — ${a.tag}`}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <span className="absolute top-4 left-4 font-editorial italic text-white/90 text-lg drop-shadow-lg">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </Link>
                  </div>

                  <div className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
                    <span className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                      {a.tag.toUpperCase()}
                    </span>
                    <h2 className="font-display font-900 tracking-tighter leading-none text-4xl md:text-5xl mt-3">
                      {a.name}
                    </h2>
                    <p className="font-body text-base md:text-lg text-black/75 leading-relaxed mt-5">
                      {a.blurb}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-6">
                      {a.highlights.map((h) => (
                        <span
                          key={h}
                          className="inline-flex items-center gap-1.5 font-body font-700 text-sm bg-white border border-zinc-200 rounded-full px-3 py-1.5"
                        >
                          <MapPin size={13} className="text-[#DF3226]" />
                          {h}
                        </span>
                      ))}
                    </div>
                    <Link
                      to={`/areas/${a.id}`}
                      data-testid={`area-explore-${a.id}`}
                      className="mt-7 inline-flex items-center gap-2 font-display font-800 bg-[#DF3226] text-white px-6 py-3 rounded-full border border-zinc-200 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
                    >
                      Explore {a.name}
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Know the areas?<br />
            <span className="font-editorial italic font-600">Now build the trip</span>
          </h2>
          <Link
            to="/planner"
            data-testid="areas-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full border border-white/20 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            Open the planner
          </Link>
        </div>
      </section>

      <GygTourBand tourId="1297085" />

      <ExploreLinks
        testid="explore-areas"
        links={[
          { label: "Shopping streets beyond Strøget — browse like a local", to: "/copenhagen-shopping-streets" },
          { label: "Where to stay — hand-picked hotels by area", to: "/stay" },
          { label: "Build your trip — the free itinerary planner", to: "/planner" },
          { label: "Getting around — the Copenhagen transport guide", to: "/transport" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
